/**
 * NotificationStreamAssembler — reconstructs BitchatPacket frames from a
 * stream of BLE notifications.
 *
 * BLE notifications do NOT carry packet boundaries. A single
 * `characteristicvaluechanged` event may contain part of a packet, one
 * complete packet, or several concatenated packets. iOS Bitchat writes
 * straight to `updateValue()` which is then chunked by CoreBluetooth to
 * the negotiated ATT MTU (20 bytes by default, up to ~244 after exchange).
 *
 * The assembler keeps a running buffer. After each `feed(chunk)` it walks
 * the buffer trying to parse a BitchatPacket header. If the header gives
 * a complete packet length that's <= buffer size, the packet is emitted
 * and the buffer slides forward. Partial packets remain for the next
 * feed().
 *
 * Hard caps and timeouts mirror iOS's NotificationStreamAssembler.swift:
 *   - Max buffer size:   8 MiB
 *   - Stall timeout:     250 ms between chunks (if exceeded, buffer reset)
 */

const V1_HEADER_SIZE = 14;
const V2_HEADER_SIZE = 16;
const PEER_ID_SIZE   = 8;
const SIGNATURE_SIZE = 64;
const MAX_BUFFER     = 8 * 1024 * 1024;
const STALL_MS       = 250;

const FLAG_HAS_RECIPIENT = 0x01;
const FLAG_HAS_SIGNATURE = 0x02;
const FLAG_IS_COMPRESSED = 0x04;
const FLAG_HAS_ROUTE     = 0x08;

export class NotificationStreamAssembler {
  /**
   * @param {(packetBytes: Uint8Array) => void} onPacket
   */
  constructor(onPacket) {
    this.onPacket = onPacket;
    this.buffer = new Uint8Array(0);
    this.lastFeedAt = 0;
  }

  /**
   * Feed a new notification chunk. Emits zero or more complete packets
   * through the onPacket callback.
   */
  feed(chunk) {
    const now = Date.now();
    if (this.buffer.length && now - this.lastFeedAt > STALL_MS) {
      // Stall: abandon whatever was mid-flight.
      console.warn('[StreamAssembler] stall reset (%d bytes discarded)', this.buffer.length);
      this.buffer = new Uint8Array(0);
    }
    this.lastFeedAt = now;

    this.buffer = _concat(this.buffer, chunk);
    if (this.buffer.length > MAX_BUFFER) {
      console.warn('[StreamAssembler] buffer overflow, resetting');
      this.buffer = new Uint8Array(0);
      return;
    }

    // Walk the buffer, emitting packets as they become complete.
    while (true) {
      const n = _frameSize(this.buffer);
      if (n === null) break;            // not enough bytes to decide yet
      if (n === 0) {                    // garbage — drop one byte and retry
        this.buffer = this.buffer.subarray(1);
        if (this.buffer.length === 0) break;
        continue;
      }
      if (n > this.buffer.length) break; // wait for more bytes
      const frame = this.buffer.subarray(0, n);
      this.buffer = this.buffer.subarray(n);
      try {
        this.onPacket(new Uint8Array(frame));
      } catch (err) {
        console.warn('[StreamAssembler] consumer threw:', err);
      }
    }
  }

  reset() {
    this.buffer = new Uint8Array(0);
    this.lastFeedAt = 0;
  }
}

/**
 * Given a candidate buffer, return the length of the first BitchatPacket
 * frame, or null if we can't tell yet, or 0 if the bytes are obviously
 * garbage (unparseable header, version byte 0, etc).
 *
 * This must stay in sync with BinaryProtocol.swift in the Bitchat repo.
 */
function _frameSize(buf) {
  if (buf.length < V1_HEADER_SIZE) return null;

  const version = buf[0];
  if (version !== 1 && version !== 2) return 0;

  const headerSize = version === 2 ? V2_HEADER_SIZE : V1_HEADER_SIZE;
  if (buf.length < headerSize) return null;

  const flags = buf[11];
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const payloadLength = version === 2
    ? view.getUint32(12, false)
    : view.getUint16(12, false);

  // Rough sanity: reject absurd declared sizes so a random byte stream
  // can't make us allocate gigabytes waiting for a phantom packet.
  if (payloadLength > 16 * 1024 * 1024) return 0;

  let size = headerSize + PEER_ID_SIZE;
  if (flags & FLAG_HAS_RECIPIENT) size += PEER_ID_SIZE;

  // v2 route: 1-byte hop count + hops × 8-byte peer IDs, inserted before
  // the payload.
  if (version === 2 && (flags & FLAG_HAS_ROUTE)) {
    if (buf.length < size + 1) return null;
    const hopCount = buf[size];
    size += 1 + hopCount * PEER_ID_SIZE;
  }

  size += payloadLength;
  if (flags & FLAG_HAS_SIGNATURE) size += SIGNATURE_SIZE;

  return size;
}

function _concat(a, b) {
  if (!a.length) return new Uint8Array(b);
  if (!b.length) return a;
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

export default NotificationStreamAssembler;
